import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ReplyIcon from "@mui/icons-material/Reply";
import SearchIcon from "@mui/icons-material/Search";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link as RouterLink,
  useLoaderData,
  useSearchParams,
} from "react-router-dom";
import { Chat } from "../chat/type";
import { ChatConversationData } from "./laoder";

// Gönderen bazlı renkler: aynı kişinin mesajları aynı renkle işaretlenir,
// böylece ref verilmemiş cevaplarda kimin kiminle konuştuğu takip edilebilir.
const SENDER_COLORS = [
  "#1976d2",
  "#d32f2f",
  "#2e7d32",
  "#ed6c02",
  "#9c27b0",
  "#0288d1",
  "#c2185b",
  "#5d4037",
  "#00796b",
  "#7b1fa2",
  "#f9a825",
  "#455a64",
];

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatDay = (d: Date) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return "Bugün";
  if (sameDay(d, yesterday)) return "Dün";
  return d.toLocaleDateString("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatTime = (d: Date) =>
  d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

const initials = (name: string) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => (p[0] ? p[0].toLocaleUpperCase("tr-TR") : ""))
    .join("");

const senderKeyOf = (c: Chat) => c.creatorId || c.fullName;

export default function ChatConversation() {
  const { hospital, chats } = useLoaderData() as ChatConversationData;
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("focus");

  const [search, setSearch] = useState("");
  const [senderFilter, setSenderFilter] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  const [pendingJump, setPendingJump] = useState<string | null>(focusId);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const byId = useMemo(() => {
    const map = new Map<string, Chat>();
    chats.forEach((c) => map.set(c.id, c));
    return map;
  }, [chats]);

  const replyCount = useMemo(() => {
    const map = new Map<string, number>();
    chats.forEach((c) => {
      if (c.refChatId) map.set(c.refChatId, (map.get(c.refChatId) ?? 0) + 1);
    });
    return map;
  }, [chats]);

  const senders = useMemo(() => {
    const colors = new Map<string, string>();
    const names = new Map<string, string>();
    chats.forEach((c) => {
      const key = senderKeyOf(c);
      if (!colors.has(key)) {
        colors.set(key, SENDER_COLORS[colors.size % SENDER_COLORS.length]);
        names.set(key, c.fullName);
      }
    });
    return { colors, names };
  }, [chats]);

  const visible = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    return chats.filter((c) => {
      if (senderFilter && senderKeyOf(c) !== senderFilter) return false;
      if (
        q &&
        !c.message.toLocaleLowerCase("tr-TR").includes(q) &&
        !c.fullName.toLocaleLowerCase("tr-TR").includes(q)
      )
        return false;
      return true;
    });
  }, [chats, search, senderFilter]);

  const focusVisible = useMemo(
    () => !!focusId && visible.some((c) => c.id === focusId),
    [visible, focusId]
  );

  const jumpTo = useCallback(
    (id: string) => {
      if (!byId.has(id)) return;
      // Filtre yüzünden görünmüyorsa filtreleri temizle; render sonrası kaydırılır
      if (!visible.some((c) => c.id === id)) {
        setSearch("");
        setSenderFilter(null);
      }
      setPendingJump(id);
    },
    [byId, visible]
  );

  useEffect(() => {
    if (!pendingJump) return;
    const el = itemRefs.current[pendingJump];
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    setFlashId(pendingJump);
    setPendingJump(null);
    const t = window.setTimeout(() => setFlashId(null), 2000);
    return () => window.clearTimeout(t);
  }, [pendingJump, visible]);

  const scrollToEdge = (edge: "top" | "bottom") => {
    if (visible.length === 0) return;
    const target = edge === "top" ? visible[0] : visible[visible.length - 1];
    setPendingJump(target.id);
  };

  const focusChat = focusId ? byId.get(focusId) : undefined;

  return (
    <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1000px" } }}>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
        >
          <Box>
            <Typography variant="h5" component="h1">
              {hospital?.hospitalName ?? "Hastane yazışması"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {chats.length} mesaj · {senders.colors.size} kişi
              {visible.length !== chats.length &&
                ` · ${visible.length} tanesi gösteriliyor`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {focusChat && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<MyLocationIcon />}
                onClick={() => jumpTo(focusChat.id)}
              >
                Seçili mesaja git
              </Button>
            )}
            <Tooltip title="En başa">
              <IconButton size="small" onClick={() => scrollToEdge("top")}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="En sona">
              <IconButton size="small" onClick={() => scrollToEdge("bottom")}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Mesajlarda ara"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {senderFilter && (
            <Chip
              size="small"
              label={`Sadece: ${senders.names.get(senderFilter) ?? senderFilter}`}
              onDelete={() => setSenderFilter(null)}
              deleteIcon={<FilterAltOffIcon />}
              sx={{
                bgcolor: alpha(senders.colors.get(senderFilter) ?? "#999", 0.15),
                whiteSpace: "nowrap",
              }}
            />
          )}
        </Stack>
      </Stack>

      {visible.length === 0 && (
        <Typography color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
          {chats.length === 0
            ? "Bu hastanede henüz mesaj yok."
            : "Filtreye uyan mesaj bulunamadı."}
        </Typography>
      )}

      <Stack spacing={1}>
        {visible.map((chat, idx) => {
          const date = new Date(chat.creationTime);
          const prev = idx > 0 ? new Date(visible[idx - 1].creationTime) : null;
          const showDay = !prev || !sameDay(prev, date);
          const senderKey = senderKeyOf(chat);
          const color = senders.colors.get(senderKey) ?? "#999";
          const isFocused = chat.id === focusId;
          const isFlash = chat.id === flashId;
          const ref =
            chat.refChat ?? (chat.refChatId ? byId.get(chat.refChatId) : null);
          const refInList = !!chat.refChatId && byId.has(chat.refChatId);
          const replies = replyCount.get(chat.id) ?? 0;

          return (
            <React.Fragment key={chat.id}>
              {showDay && (
                <Divider sx={{ my: 1 }}>
                  <Chip size="small" label={formatDay(date)} />
                </Divider>
              )}
              <Paper
                ref={(el: HTMLDivElement | null) => {
                  itemRefs.current[chat.id] = el;
                }}
                id={`chat-${chat.id}`}
                variant="outlined"
                sx={(theme) => ({
                  p: 1.5,
                  borderLeft: 4,
                  borderLeftColor: color,
                  transition: "background-color 0.6s ease",
                  bgcolor: isFlash
                    ? alpha(theme.palette.primary.main, 0.18)
                    : isFocused
                    ? alpha(theme.palette.warning.main, 0.14)
                    : undefined,
                  outline: isFocused
                    ? `2px solid ${theme.palette.warning.main}`
                    : undefined,
                })}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "flex-start" }}
                >
                  <Tooltip title="Sadece bu kişinin mesajlarını göster">
                    <Avatar
                      onClick={() =>
                        setSenderFilter((cur) =>
                          cur === senderKey ? null : senderKey
                        )
                      }
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: 13,
                        bgcolor: color,
                        cursor: "pointer",
                      }}
                    >
                      {initials(chat.fullName)}
                    </Avatar>
                  </Tooltip>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: "center", flexWrap: "wrap" }}
                    >
                      {chat.creatorId ? (
                        <Link
                          component={RouterLink}
                          to={`/users/${chat.creatorId}`}
                          sx={{ fontWeight: 600, color }}
                        >
                          {chat.fullName}
                        </Link>
                      ) : (
                        <Typography sx={{ fontWeight: 600, color }}>
                          {chat.fullName}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(date)}
                      </Typography>
                      {replies > 0 && (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<ReplyIcon />}
                          label={`${replies} yanıt`}
                        />
                      )}
                      {isFocused && (
                        <Chip
                          size="small"
                          color="warning"
                          label="Seçili mesaj"
                        />
                      )}
                      <Box sx={{ flex: 1 }} />
                      <Link
                        component={RouterLink}
                        to={`/chats/${chat.id}`}
                        variant="caption"
                      >
                        Detay
                      </Link>
                    </Stack>

                    {ref && (
                      <Box
                        onClick={
                          refInList
                            ? () => jumpTo(chat.refChatId as string)
                            : undefined
                        }
                        sx={(theme) => ({
                          mt: 0.5,
                          mb: 0.5,
                          px: 1,
                          py: 0.5,
                          borderLeft: 3,
                          borderColor:
                            senders.colors.get(senderKeyOf(ref)) ??
                            theme.palette.divider,
                          bgcolor: alpha(theme.palette.text.primary, 0.04),
                          borderRadius: 1,
                          cursor: refInList ? "pointer" : "default",
                          "&:hover": refInList
                            ? {
                                bgcolor: alpha(theme.palette.text.primary, 0.08),
                              }
                            : undefined,
                        })}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {ref.fullName}
                          {!refInList && " (listede yok / silinmiş)"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {ref.message}
                        </Typography>
                      </Box>
                    )}

                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {chat.message}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </React.Fragment>
          );
        })}
      </Stack>

      {focusChat && !focusVisible && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, textAlign: "center" }}
        >
          Seçili mesaj mevcut filtreyle gizlendi.{" "}
          <Link component="button" onClick={() => jumpTo(focusChat.id)}>
            Filtreyi temizle ve göster
          </Link>
        </Typography>
      )}
    </Box>
  );
}
